import { useId } from "react";

// Small inline SVG icon set (20x20 grid, stroke-based outline style) —
// replaces emoji throughout the app to match anyainow's icon approach:
// currentColor-driven so icons follow text color / theme automatically,
// sized via the .icon CSS class (see styles.css) instead of fixed pixels.
const base = {
  viewBox: "0 0 20 20",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

export function SunIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <circle cx="10" cy="10" r="3.25" fill="currentColor" stroke="none" />
      <path d="M10 1.5v2.25M10 16.25v2.25M1.5 10h2.25M16.25 10h2.25M4.2 4.2l1.6 1.6M14.2 14.2l1.6 1.6M4.2 15.8l1.6-1.6M14.2 5.8l1.6-1.6" />
    </svg>
  );
}

export function MoonIcon({ className }) {
  const maskId = useId();
  return (
    <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
      <mask id={maskId}>
        <rect width="20" height="20" fill="white" />
        <circle cx="13.5" cy="6.5" r="6" fill="black" />
      </mask>
      <circle cx="10" cy="10" r="7.5" fill="currentColor" mask={`url(#${maskId})`} />
    </svg>
  );
}

// "System/Auto" theme option — a monitor glyph, matching anyainow's
// System -> Light -> Dark theme-cycle icon set.
export function MonitorIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <rect x="2" y="3" width="16" height="11" rx="1.5" />
      <path d="M7 17.5h6M10 14v3.5" />
    </svg>
  );
}

export function LogoutIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <path d="M8 3H4.5A1.5 1.5 0 003 4.5v11A1.5 1.5 0 004.5 17H8" />
      <path d="M13 13.5L17 10l-4-3.5" />
      <path d="M17 10H8" />
    </svg>
  );
}

// Corner-bracket "scan/viewfinder" glyph — used for the scan balance badge.
export function ScanIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <path d="M3 7V4.5A1.5 1.5 0 014.5 3H7" />
      <path d="M13 3h2.5A1.5 1.5 0 0117 4.5V7" />
      <path d="M17 13v2.5a1.5 1.5 0 01-1.5 1.5H13" />
      <path d="M7 17H4.5A1.5 1.5 0 013 15.5V13" />
    </svg>
  );
}

export function CameraIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <path d="M3 7.5A1.5 1.5 0 014.5 6h1.2l.9-1.5h6.8L14.3 6h1.2A1.5 1.5 0 0117 7.5v7A1.5 1.5 0 0115.5 16h-11A1.5 1.5 0 013 14.5v-7z" />
      <circle cx="10" cy="10.5" r="3" />
    </svg>
  );
}

export function FolderIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <path d="M3 6a1.5 1.5 0 011.5-1.5h3l1.5 2h6.5A1.5 1.5 0 0117 8v6a1.5 1.5 0 01-1.5 1.5h-11A1.5 1.5 0 013 14V6z" />
    </svg>
  );
}

export function CarIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <path d="M3.5 12l1.4-4.3A1.5 1.5 0 016.3 6.7h7.4a1.5 1.5 0 011.4 1l1.4 4.3" />
      <path d="M3 12h14v2.3a.7.7 0 01-.7.7h-.8a1 1 0 01-1-1v-.5H5.5V14a1 1 0 01-1 1h-.8a.7.7 0 01-.7-.7V12z" />
      <circle cx="6.2" cy="14.5" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="13.8" cy="14.5" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function PinIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <path d="M10 17.5S16 11.5 16 7.5a6 6 0 10-12 0c0 4 6 10 6 10z" />
      <circle cx="10" cy="7.5" r="2" />
    </svg>
  );
}

export function RulerIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <rect x="2.5" y="7" width="15" height="6" rx="1" />
      <path d="M6 7v2M9 7v3M12 7v2M15 7v3" />
    </svg>
  );
}

export function GearIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true" strokeWidth="1.75">
      <circle cx="10" cy="10" r="2.5" />
      <path d="M10 4.5v1.4M10 14.1v1.4M4.5 10h1.4M14.1 10h1.4M6 6l1 1M13 13l1 1M14 6l-1 1M7 13l-1 1" />
    </svg>
  );
}

export function FilterIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <path d="M3 5h14M6 10h8M8.5 15h3" />
    </svg>
  );
}

export function MapIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <path d="M3 5.5l4.5-1.5 5 1.5 4.5-1.5v10.5l-4.5 1.5-5-1.5L3 16V5.5z" />
      <path d="M7.5 4v10.5M12.5 5.5V16" />
    </svg>
  );
}

export function PencilIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <path d="M13.5 3.5l3 3L6 17H3v-3L13.5 3.5z" />
    </svg>
  );
}

export function XIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <path d="M5 5l10 10M15 5L5 15" />
    </svg>
  );
}

export function CheckCircleIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <circle cx="10" cy="10" r="7.25" />
      <path d="M7 10.4l2 2 4-4.5" />
    </svg>
  );
}

export function DownloadIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <path d="M10 3v9.5M6.5 9l3.5 3.5L13.5 9" />
      <path d="M3.5 14v1.5A1.5 1.5 0 005 17h10a1.5 1.5 0 001.5-1.5V14" />
    </svg>
  );
}

// Small spreadsheet/table grid — distinguishes a CSV export from the PDF
// download (DownloadIcon) sitting next to it.
export function TableIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <rect x="3" y="4" width="14" height="12" rx="1.5" />
      <path d="M3 8.5h14M8 8.5V16" />
    </svg>
  );
}

export function MailIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <rect x="2.5" y="4.5" width="15" height="11" rx="1.5" />
      <path d="M3.2 5.5L10 11l6.8-5.5" />
    </svg>
  );
}

export function ArchiveIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <rect x="2.5" y="3.5" width="15" height="4" rx="1" />
      <path d="M4 7.5v7A1.5 1.5 0 005.5 16h9a1.5 1.5 0 001.5-1.5v-7" />
      <path d="M8 10.5h4" />
    </svg>
  );
}

export function UnarchiveIcon({ className }) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <path d="M4 8.5a1 1 0 011-1h10a1 1 0 011 1V15a1.5 1.5 0 01-1.5 1.5h-9A1.5 1.5 0 014 15V8.5z" />
      <path d="M10 6v6M7.5 9.5L10 7l2.5 2.5" />
    </svg>
  );
}
